import { shallowEqual, useSelector } from 'react-redux';

import { selectedAddress } from 'state/selected-address/selected-address-slice';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import capitalize from 'lodash/capitalize';
import { selectSlice as selectConsumption } from 'state/consumption/consumption-slice';
import { usePartner } from 'components/app/routes/usePartner';
import { useMaskPII } from 'hooks/useMaskPII';
import { useNavigation } from 'hooks/useNavigation';
import NsColumn from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn';
import NsContent from 'components/common/ns-content/NsContent';
import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsAlert } from 'components/common/ns-alert/NsAlert';
import { NsSecondaryCta } from 'components/common/ns-secondary-cta/NsSecondaryCta';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import { QuoteByEmailConfirmationMsg } from './QuoteByEmailConfirmationMsg';
import { EnergyHelp } from './EnergyHelp';
import { MoveInTariffInfo } from 'components/direct-sales/quote/components/MoveInTariffInfo';
import { FUEL_OPTIONS, PARTNERS } from 'config/app-config';
import { formatEnergy } from 'utils/numberFormat';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { selectData } from 'state/abandoned-cart/abandoned-cart-slice'
import { AbandonedCartStatus } from 'state/abandoned-cart/types/abandoned-cart';
import AbandonedCartGreeting from '../../../abandoned-cart/components/AbandonedCartGreeting.tsx';

export const testIdVoicesalesWindbackAlert = 'testIdVoicesalesWinbackAlert';
export const editConsumptionTestId = 'editConsumptionTestId';

const formatEnergyOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
};

type FuelConsumptionProps = {
  fuelType?: boolean;
  fuelName?: 'Electricity' | 'Gas';
  consumption?: number;
};

const FuelConsumption = ({
  fuelType,
  fuelName,
  consumption
}: FuelConsumptionProps) => {
  if (!fuelType) {
    return null;
  }

  return (
    <>
      <strong>{fuelName}: </strong>
      {formatEnergy(consumption, 'kwh', formatEnergyOptions)}{' '}
    </>
  );
};

export const QuoteHeader = () => {
  const { next, eventTypes } = useNavigation();


  const { mask } = useMaskPII();
  const address = useSelector(selectedAddress, shallowEqual);
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const consumption = useSelector(selectConsumption, shallowEqual);
  const abandonedCartStatus = useSelector(selectData, shallowEqual).status;
  const { activePartner, creditValues } = usePartner();
  const isMoveInJourney = activePartner[PARTNERS.MOVE_IN];
  const fuelValue = getFuelValue(fuelType);
  const isAgentSalesJourney = useIsLoginEnabledJourney();
  const shouldShowEnergyHelp = !isAgentSalesJourney && !isMoveInJourney;

  const showAbandonedCartGreeting =
    [AbandonedCartStatus.RETURNINPROGRESS, AbandonedCartStatus.RETURNED].includes(abandonedCartStatus);

  const fuelTypeText =
    fuelValue === FUEL_OPTIONS.DUAL
      ? 'Gas and electricity'
      : capitalize(fuelValue);

  const displayedCreditValues = creditValues
    .filter((value) => value > 0)
    .reduce((acc, value, index, values) => {
      return index === values.length - 2
        ? `${acc} £${value} and`
        : `${acc} £${value},`;
    }, '');

  const isVoiceSalesWinbackJourney = activePartner[PARTNERS.VOICESALES_WINBACK];

  const editConsumption = () => {
    next({ eventType: eventTypes.CONSUMPTION, wasVisited: true });
  };

  return (
    <NsColumn hockeypitch="2:1">
      <NsContent>
        <NsContent>
          <NsBackCta />
          <QuoteByEmailConfirmationMsg />
        </NsContent>
        <NsContent spacing="compact">
          <h5 className="p-small">
            <strong>
              Quote for{' '}
              <span {...mask}>
                {address?.displayName}, {address?.postcode}
              </span>
            </strong>
          </h5>
          { showAbandonedCartGreeting
            ? <AbandonedCartGreeting />
            : <NsTitle title="Available energy tariffs for you" />
          }
          {isVoiceSalesWinbackJourney && (
            <NsAlert
              state="info"
              heading="Ask customer whenever they are unsure about the tariff"
              testId={testIdVoicesalesWindbackAlert}
            >
              <p>
                We can offer Credit to the customer to go on their account 3
                months after their Supply Start Date. Please use one of the
                amounts available : {displayedCreditValues}
              </p>
            </NsAlert>
          )}
        </NsContent>
        <NsContent spacing="compact">
          <p>
            <strong>Fuel type:</strong> {fuelTypeText}
          </p>
          <p>
            <strong>Energy estimation: </strong>
            <FuelConsumption
              fuelType={fuelType.electric}
              fuelName="Electricity"
              consumption={consumption.electric}
            />
            <FuelConsumption
              fuelType={fuelType.gas}
              fuelName="Gas"
              consumption={consumption.gas}
            />

            {!isMoveInJourney && (
              <NsSecondaryCta
                icon=""
                onClick={editConsumption}
                testId={editConsumptionTestId}
              >
                Edit
              </NsSecondaryCta>
            )}
          </p>
        </NsContent>
      </NsContent>
      <EnergyHelp shouldShow={shouldShowEnergyHelp} />
      <MoveInTariffInfo shouldShow={isMoveInJourney} />
    </NsColumn>
  );
};
